/**
 * Impressions de la clinique : une seule mise en page pour tous les documents
 * (reçus, factures, ordonnances, fiches, listes), la même que les PDF produits
 * par le serveur (comptes rendus, certificats, bordereaux).
 *
 *  - en-tête : logo, nom, adresse et téléphone de la clinique, titre du document
 *    et son numéro / sa date à droite, filet bleu ;
 *  - corps : fourni par l'écran, avec les classes communes ci-dessous ;
 *  - signature (facultative) et pied de page « Imprimé le … par … ».
 *
 * L'impression passe par un cadre invisible : pas de fenêtre surgissante
 * (souvent bloquée par le navigateur) et rien à fermer ensuite.
 *
 * Classes disponibles dans le corps :
 *   .bloc            tableau d'informations (2 ou 4 colonnes, sans grille)
 *   table.lignes     tableau de lignes (actes, médicaments, paiements…)
 *   .droite / .centre alignement d'une cellule
 *   .total           ligne de total (gras, fond clair)
 *   .section         petit titre de partie
 *   .muted           texte gris        .anormal  valeur hors normes (rouge)
 *   .texte           paragraphe libre (garde les retours à la ligne)
 *   .encadre         cadre d'observation / remarque
 *   .badge-ok / .badge-ko  étiquettes Payé / Non payé
 */
import api, { API_BASE } from '@/api'
import store from '@/store'
import { MARQUE } from '@/marque'

let configEnCache = null

/** Configuration de la clinique (nom, logo, adresse…), chargée une fois. */
export async function configClinique(forcer = false) {
  if (configEnCache && !forcer) return configEnCache
  try {
    const res = await api.getItems('configurations')
    configEnCache = res.data?.data?.[0] || res.data?.['hydra:member']?.[0] || {}
    // Gardée sur le poste pour imprimer l'en-tête même sans connexion
    try { localStorage.setItem('config-clinique', JSON.stringify({ nom: configEnCache.nom, adresse: configEnCache.adresse, ville: configEnCache.ville, telephone: configEnCache.telephone, email: configEnCache.email, logo: configEnCache.logo })) } catch (x) { /* rien */ }
  } catch (e) {
    try { configEnCache = JSON.parse(localStorage.getItem('config-clinique') || '{}') } catch (x) { configEnCache = {} }
  }
  return configEnCache
}

/** Adresse complète d'un fichier envoyé au serveur (logo…). */
export function urlFichier(chemin) {
  if (!chemin) return ''
  if (/^(https?:|data:)/.test(chemin)) return chemin
  return `${API_BASE}${chemin.startsWith('/') ? '' : '/'}${chemin}`
}

/** Échappe un texte pour l'insérer dans le HTML imprimé. */
export function e(valeur) {
  if (valeur === null || valeur === undefined) return ''
  return String(valeur)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** 12500 -> « 12 500 FCFA » */
export function fcfa(v) {
  const n = Number(v || 0)
  return new Intl.NumberFormat('fr-FR').format(Math.round(n)).replace(/ | /g, ' ') + ' FCFA'
}

/** Date au format 02/10/2026 (et l'heure si demandé). */
export function dateFr(d, avecHeure = false) {
  if (!d) return ''
  const date = d instanceof Date ? d : new Date(String(d).replace(' ', 'T'))
  if (isNaN(date.getTime())) return String(d)
  const j = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const base = `${j}/${m}/${date.getFullYear()}`
  if (!avecHeure) return base
  return `${base} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** Âge en années à partir de la date de naissance. */
export function age(dateNaissance) {
  if (!dateNaissance) return ''
  const n = new Date(String(dateNaissance).replace(' ', 'T'))
  if (isNaN(n.getTime())) return ''
  const a = new Date()
  let ans = a.getFullYear() - n.getFullYear()
  if (a.getMonth() < n.getMonth() || (a.getMonth() === n.getMonth() && a.getDate() < n.getDate())) ans--
  return ans >= 0 ? `${ans} an${ans > 1 ? 's' : ''}` : ''
}

/** Nom complet d'une personne (patient, médecin, caissier), sans « undefined ». */
export function nomComplet(p, avecTitre = '') {
  if (!p) return ''
  if (typeof p === 'string') return p
  const n = [p.prenom, p.nom].filter(Boolean).join(' ').trim()
  return n ? `${avecTitre}${n}` : ''
}

/** Bloc d'informations : [[libellé, valeur], …] sur 2 colonnes de paires. */
export function blocInfos(paires, colonnes = 2) {
  const lignes = paires.filter(p => p && p[1] !== undefined && p[1] !== null && p[1] !== '')
  let html = '<table class="bloc">'
  for (let i = 0; i < lignes.length; i += colonnes) {
    html += '<tr>'
    for (let k = 0; k < colonnes; k++) {
      const p = lignes[i + k]
      html += p ? `<td class="lib">${e(p[0])}</td><td>${p[2] ? p[1] : e(p[1])}</td>` : '<td></td><td></td>'
    }
    html += '</tr>'
  }
  return html + '</table>'
}

export const STYLES = `
  @page { size: A4; margin: 12mm 12mm 14mm 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "DejaVu Sans", "Segoe UI", Arial, sans-serif; font-size: 11.5px; color: #222; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 100%; }
  .entete { width: 100%; border-collapse: collapse; margin-bottom: 14px; border-bottom: 2px solid #0d6efd; }
  .entete td { vertical-align: top; padding: 0 0 6px 0; }
  .entete .logo { width: 78px; }
  .entete .logo img { max-height: 64px; max-width: 72px; }
  .clinique { font-size: 16px; font-weight: bold; color: #0d3c78; }
  .coord { color: #444; line-height: 1.45; }
  .titre-doc { text-align: right; }
  .titre-doc .titre { font-size: 16px; font-weight: bold; text-transform: uppercase; }
  .titre-doc .ref { font-size: 11px; color: #444; margin-top: 3px; line-height: 1.45; }
  .section { font-size: 12.5px; font-weight: bold; color: #0d3c78; margin: 14px 0 6px; padding-bottom: 2px; border-bottom: 1px solid #d6e2f0; }
  table.bloc { width: 100%; border-collapse: collapse; margin-bottom: 12px; border: 1px solid #ddd; }
  table.bloc td { padding: 4px 8px; vertical-align: top; }
  table.bloc td.lib { color: #666; white-space: nowrap; width: 1%; }
  table.lignes { width: 100%; border-collapse: collapse; margin-top: 6px; page-break-inside: auto; }
  table.lignes tr { page-break-inside: avoid; }
  table.lignes th { background: #e9f1f9; border: 1px solid #bbb; padding: 5px; text-align: left; font-weight: bold; }
  table.lignes td { border: 1px solid #ddd; padding: 5px; vertical-align: top; }
  table.lignes tfoot td, table.lignes tr.total td { font-weight: bold; background: #f3f7fb; }
  .droite { text-align: right; } .centre { text-align: center; }
  .muted { color: #777; } .anormal { color: #b00020; font-weight: bold; }
  .texte { line-height: 1.6; font-size: 12px; white-space: pre-line; }
  .encadre { border: 1px solid #ddd; background: #fafafa; padding: 8px 10px; margin-top: 10px; }
  .totaux { width: 45%; margin-left: auto; border-collapse: collapse; margin-top: 10px; }
  .totaux td { padding: 4px 8px; border-bottom: 1px solid #eee; }
  .totaux tr.grand td { font-size: 13px; font-weight: bold; border-top: 2px solid #0d6efd; }
  .badge-ok, .badge-ko { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; }
  .badge-ok { background: #d1e7dd; color: #0f5132; } .badge-ko { background: #f8d7da; color: #842029; }
  .signature { width: 100%; margin-top: 36px; border-collapse: collapse; page-break-inside: avoid; }
  .signature td { width: 50%; vertical-align: top; padding-top: 4px; }
  .signature .zone { height: 60px; }
  .pied { margin-top: 18px; padding-top: 4px; border-top: 1px solid #ddd; font-size: 9px; color: #888; text-align: center; }
  /* Format A5 (reçus) */
  .format-A5 { font-size: 10.5px; }

  /* Tickets de caisse (imprimantes thermiques 58 et 80 mm) : noir pur, sans
     fond ni gris (mal rendus par la chauffe), séparateurs en pointillés */
  .ticket { margin: 0 auto; padding: 0 1mm 2mm; font-family: Arial, Helvetica, sans-serif; line-height: 1.3; }
  .ticket80 { width: 72mm; font-size: 12px; }
  .ticket58 { width: 48mm; font-size: 10.5px; }
  .ticket, .ticket * { color: #000 !important; background: transparent !important; box-shadow: none !important; }
  .ticket .entete { border-bottom: 1px dashed #000; margin-bottom: 5px; }
  .ticket .entete tr, .ticket .entete td { display: block; width: 100%; text-align: center !important; padding: 0 0 2px; }
  .ticket .entete .logo img { max-height: 12mm; max-width: 32mm; filter: grayscale(1) contrast(1.3); }
  .ticket .clinique { font-size: 1.25em; text-transform: uppercase; }
  .ticket .coord { line-height: 1.3; }
  .ticket .titre-doc .titre { font-size: 1.1em; margin-top: 3px; }
  .ticket .titre-doc .ref { font-size: 1em; }
  .ticket .section { font-size: 1em; margin: 6px 0 3px; border-bottom: 1px dashed #000; }
  .ticket table.bloc { border: 0; margin-bottom: 4px; }
  .ticket table.bloc td { padding: 1px 0; }
  .ticket table.bloc td.lib { white-space: normal; width: 40%; padding-right: 4px; }
  .ticket table.lignes { margin-top: 3px; }
  .ticket table.lignes th, .ticket table.lignes td { border: 0; border-bottom: 1px dashed #000; padding: 2px 0; }
  .ticket table.lignes th { font-weight: bold; }
  .ticket .totaux { width: 100%; margin-top: 4px; }
  .ticket .totaux td { padding: 1px 0; border: 0; }
  .ticket .totaux tr.grand td { font-size: 1.15em; border-top: 1px solid #000; padding-top: 3px; }
  .ticket .encadre { border: 1px dashed #000; padding: 3px 4px; margin-top: 5px; }
  .ticket .badge-ok, .ticket .badge-ko { border: 1px solid #000; padding: 0 3px; font-size: .9em; }
  .ticket .signature { display: none; }
  .ticket .pied { border-top: 1px dashed #000; margin-top: 6px; padding-top: 3px; font-size: .85em; }
  .ticket .grand-numero { font-size: 2.6em; font-weight: bold; line-height: 1.1; }
  .ticket .merci { text-align: center; margin-top: 6px; }
`

const TAILLES = {
  A4: { page: 'A4', marge: '12mm 12mm 14mm 12mm' },
  A5: { page: 'A5', marge: '8mm' },
  // Rouleaux thermiques : la hauteur de page suit le contenu (voir imprimer())
  ticket80: { largeur: 80, marge: '0' },
  ticket58: { largeur: 58, marge: '0' },
}

const CLE_TICKET = 'keneya.formatTicket'

/** Largeur du rouleau de l'imprimante de ce poste : 'ticket80' (par défaut) ou 'ticket58'. */
export function formatTicket() {
  try { return localStorage.getItem(CLE_TICKET) === 'ticket58' ? 'ticket58' : 'ticket80' } catch (e) { return 'ticket80' }
}

/** Retient la largeur du rouleau pour ce poste (tous les tickets l'utilisent ensuite). */
export function choisirFormatTicket(format) {
  try { localStorage.setItem(CLE_TICKET, format === 'ticket58' ? 'ticket58' : 'ticket80') } catch (e) { /* poste sans stockage */ }
}

/** 'ticket' (largeur du poste), 'ticket58' ou 'ticket80' → format réel. */
export function resoudreFormat(format) {
  if (format === 'ticket') return formatTicket()
  return TAILLES[format] ? format : 'A4'
}

export function estTicket(format) {
  return String(format || '').startsWith('ticket')
}

/** En-tête commun (identique aux PDF du serveur). */
export function entete(config, { titre, sousTitre, numero, date } = {}) {
  const c = config || {}
  const logo = urlFichier(c.logo)
  const coord = [
    [c.adresse, c.ville].filter(Boolean).map(e).join(' — '),
    c.telephone ? `Tél. ${e(c.telephone)}` : '',
    c.email ? e(c.email) : '',
  ].filter(Boolean).join('<br>')
  const ref = [
    numero ? `N° <strong>${e(numero)}</strong>` : '',
    date ? `Date : ${e(date)}` : '',
    sousTitre ? e(sousTitre) : '',
  ].filter(Boolean).join('<br>')
  return `
    <table class="entete"><tr>
      ${logo ? `<td class="logo"><img src="${e(logo)}" alt=""></td>` : ''}
      <td><div class="clinique">${e(c.nom || 'Clinique')}</div><div class="coord">${coord}</div></td>
      <td class="titre-doc"><div class="titre">${e(titre || '')}</div>${ref ? `<div class="ref">${ref}</div>` : ''}</td>
    </tr></table>`
}

/** Zone de signature : { gauche, droite } (textes) ou true pour « Signature et cachet ». */
export function signature(sig) {
  if (!sig) return ''
  const s = sig === true ? { droite: 'Signature et cachet' } : sig
  return `<table class="signature"><tr>
      <td>${s.gauche ? `${s.gauche}<div class="zone"></div>` : ''}</td>
      <td class="droite">${s.droite ? `${s.droite}<div class="zone"></div>` : ''}</td>
    </tr></table>`
}

/** Document HTML complet (utile aussi pour l'export PDF). */
export function documentHtml({ config, titre, sousTitre, numero, date, corps, signature: sig, format = 'A4', orientation = 'portrait', note }) {
  format = resoudreFormat(format)
  const taille = TAILLES[format]
  const ticket = estTicket(format)
  // Ticket : hauteur provisoire, ajustée au contenu avant l'impression
  const page = ticket ? `${taille.largeur}mm 400mm` : `${taille.page} ${orientation}`
  const u = store.state.user
  const auteur = u ? nomComplet(u) : ''
  const maintenant = dateFr(new Date(), true)
  const pied = ticket
    ? `${note ? e(note) + '<br>' : ''}Imprimé le ${e(maintenant)}${auteur ? '<br>par ' + e(auteur) : ''}<br>${e(MARQUE.nom)}`
    : `${note ? e(note) + ' · ' : ''}Imprimé le ${e(maintenant)}${auteur ? ' par ' + e(auteur) : ''} · ${e((config && config.nom) || '')} · ${e(MARQUE.nom)}`
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${e(titre || 'Document')}${numero ? ' ' + e(numero) : ''}</title>
    <style>${STYLES} @page { size: ${page}; margin: ${taille.marge}; }</style></head>
    <body><div class="page format-${format}${ticket ? ' ticket ' + format : ''}">
      ${entete(config, { titre, sousTitre, numero, date })}
      ${corps || ''}
      ${signature(sig)}
      <div class="pied${ticket ? ' centre' : ''}">${pied}</div>
    </div></body></html>`
}

/**
 * Imprime un document.
 * @param {object} options { titre, sousTitre, numero, date, corps, signature, format: 'A4'|'A5'|'ticket', orientation, note, config }
 */
export async function imprimer(options) {
  const config = options.config || (await configClinique())
  const html = documentHtml({ ...options, config })

  const format = resoudreFormat(options.format)
  const cadre = document.createElement('iframe')
  cadre.setAttribute('aria-hidden', 'true')
  // Largeur réelle (hors écran) : la hauteur d'un ticket se mesure sur la mise en page
  cadre.style.cssText = 'position:fixed;left:-10000px;top:0;width:120mm;height:50px;border:0;visibility:hidden'
  document.body.appendChild(cadre)

  const doc = cadre.contentWindow.document
  doc.open()
  doc.write(html)
  doc.close()

  // Attendre le logo (sinon il manque à l'impression), au plus 2 secondes
  const images = Array.from(doc.images || [])
  await Promise.race([
    Promise.all(images.map(img => (img.complete ? Promise.resolve() : new Promise(r => { img.onload = img.onerror = r })))),
    new Promise(r => setTimeout(r, 2000)),
  ])

  // Ticket : page exactement aussi haute que le contenu (pas de papier perdu)
  if (estTicket(format)) {
    const contenu = doc.querySelector('.page')
    const hauteurMm = Math.ceil((contenu ? contenu.getBoundingClientRect().height : 400) * 25.4 / 96) + 6
    const style = doc.createElement('style')
    style.textContent = `@page { size: ${TAILLES[format].largeur}mm ${Math.max(40, hauteurMm)}mm; margin: 0; }`
    doc.head.appendChild(style)
  }

  const nettoyer = () => setTimeout(() => cadre.remove(), 500)
  cadre.contentWindow.addEventListener('afterprint', nettoyer)
  cadre.contentWindow.focus()
  cadre.contentWindow.print()
  setTimeout(() => { if (document.body.contains(cadre)) cadre.remove() }, 60000)
}

/**
 * Enregistre le document en PDF (même mise en page qu'à l'impression).
 * @param {object} options mêmes options qu'imprimer()
 * @param {string} nomFichier ex. « recu-CO-20261002-0004.pdf »
 */
export async function telechargerPdf(options, nomFichier = 'document.pdf') {
  const { default: html2pdf } = await import('html2pdf.js')
  const config = options.config || (await configClinique())
  const html = documentHtml({ ...options, config })
  const corps = html.substring(html.indexOf('<body>') + 6, html.lastIndexOf('</body>'))
  const conteneur = document.createElement('div')
  conteneur.style.cssText = 'position:fixed;left:-10000px;top:0;width:186mm;background:#fff'
  conteneur.innerHTML = `<style>${STYLES.replace(/@page[^}]*}/g, '')}</style>${corps}`
  document.body.appendChild(conteneur)
  try {
    const reel = resoudreFormat(options.format)
    const format = estTicket(reel) ? [TAILLES[reel].largeur, 297] : (reel === 'A5' ? 'a5' : 'a4')
    if (estTicket(reel)) conteneur.style.width = `${TAILLES[reel].largeur}mm`
    await html2pdf().set({
      margin: estTicket(reel) ? 2 : 10, filename: nomFichier,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format, orientation: options.orientation || 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    }).from(conteneur).save()
  } finally {
    conteneur.remove()
  }
}

export default { imprimer, telechargerPdf, formatTicket, choisirFormatTicket, estTicket, configClinique, documentHtml, entete, blocInfos, signature, e, fcfa, dateFr, age, nomComplet, urlFichier }
